import { useSetAtom } from "jotai";
import { useCallback } from "react";
import { ServiceType } from "@/apis/clients";
import serviceApi from "@/apis/service/serviceApi";
import { useLoading } from "@/states/loading/operations";
import { defaultServicesAtom, partCustomServicesAtom } from "./atom";

const DEFAULT_SERVICE_COMPONENT_PAYLOAD = {
  type: ServiceType.EVENT_SEMINAR,
  page: 1,
  perPage: 20,
};

/**
 * Fetch default components (20 newest) - always called on initial load
 * Saves to defaultServicesAtom
 */
export const useFetchServiceComponents = () => {
  const withLoading = useLoading();
  const setDefaultServices = useSetAtom(defaultServicesAtom);

  return useCallback(
    async (uuid: string) => {
      await withLoading(async () => {
        try {
          const res = await serviceApi.getServiceComponents({
            ...DEFAULT_SERVICE_COMPONENT_PAYLOAD,
            lpPageUuid: uuid,
          });
          setDefaultServices(() => ({
            components: res.data,
            lpPageUuid: uuid,
          }));
        } catch (e) {
          setDefaultServices((prev) => ({ ...prev }));
          throw e;
        }
      });
    },
    [withLoading, setDefaultServices],
  );
};

/**
 * Alias for clarity - fetch default data
 */
export const useFetchServiceDefault = useFetchServiceComponents;

/**
 * Fetch custom components by IDs - called when has saved serviceOrderIds
 * Saves to partCustomServicesAtom (per-part storage)
 *
 * Note: Hidden IDs should be filtered BEFORE calling this function for optimization
 *
 * @param partId - Part ID to store data
 * @param ids - Service IDs to fetch (should already be filtered)
 * @param lpPageUuid - LP page UUID
 */
export const useFetchServiceComponentsByIds = () => {
  const setPartCustomServices = useSetAtom(partCustomServicesAtom);

  return useCallback(
    async (partId: string, ids: number[], lpPageUuid: string) => {
      if (!ids || ids.length === 0 || !lpPageUuid) {
        return;
      }

      // If all IDs were filtered out, store empty array
      if (ids.length === 0) {
        setPartCustomServices((prev) => ({
          ...prev,
          [partId]: { components: [], lpPageUuid },
        }));
        return;
      }

      try {
        const res = await serviceApi.getServiceComponentsByIds({
          serviceIds: ids,
          lpPageUuid: lpPageUuid,
        });
        // Sort results to match the order of requested IDs
        const sortedData = [...res.data].sort(
          (a, b) => ids.indexOf(a.id as number) - ids.indexOf(b.id as number),
        );
        // Store per-part custom services
        setPartCustomServices((prev) => ({
          ...prev,
          [partId]: {
            components: sortedData,
            lpPageUuid: lpPageUuid,
          },
        }));
      } catch (e) {
        setPartCustomServices((prev) => ({
          ...prev,
          [partId]: { components: [], lpPageUuid },
        }));
        throw e;
      }
    },
    [setPartCustomServices],
  );
};

/**
 * Alias for clarity - fetch custom data
 */
export const useFetchServiceCustom = useFetchServiceComponentsByIds;
